import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ChevronRight, ChevronLeft, ArrowRight, Play, Lock } from 'lucide-react';
import { VideoWithFallback } from './VideoWithFallback';

const demoSlides = [
  {
    video: "https://customer-assets.emergentagent.com/job_airdiag-portal/artifacts/wg9t1iyu_Original%20video.mp4",
    poster: "/posters/aircraft-capture.jpg",
    title: "Aircraft Exterior Capture",
    description: "Technicians walk around the aircraft with a hardware-agnostic camera, capturing high-fidelity video that becomes the source-of-truth for the entire inspection."
  },
  {
    video: "https://customer-assets.emergentagent.com/job_airdiag-portal/artifacts/wy24fz5e_Copy%20of%20G200_Static_Port_Digital_Twin.mp4",
    poster: "/posters/digital-twin.jpg",
    title: "3D Digital Twin Reconstruction",
    description: "AI-driven photogrammetry rebuilds the aircraft as a precision 3D twin — enabling damage tracking, historical comparison, and virtual inspections down to the millimeter."
  },
  {
    type: "image",
    src: "https://customer-assets-jt897jd0.emergentagent.net/job_dbbb4f88-5ac8-4fde-9c1a-a326bcf136ff/artifacts/zuqtqjfp_Screenshot%202026-08-23%20at%207.04.25%E2%80%AFPM.png",
    title: "FAA-Aligned Reports",
    description: "Every inspection generates structured, traceable reports aligned with FAA Part 43.13(a) — Static-Port Flushness Analysis and Step Height Measurement Worksheets, with per-port confidence scores and calibrated tolerance checks ready for MRO handoff."
  }
];

const inspectionDemos = [
  {
    label: "Hail Damage",
    chapters: [
      {
        type: "image",
        src: "https://customer-assets-jt897jd0.emergentagent.net/job_dbbb4f88-5ac8-4fde-9c1a-a326bcf136ff/artifacts/vttnel53_IMG_9529.jpeg",
        title: "Hail Damage Capture",
        description: "Technicians capture high-fidelity imagery of the nose cone, fuselage, and leading edges immediately after a hail event — building the source-of-truth dataset for automated damage detection."
      },
      {
        type: "image",
        src: "https://customer-assets-jt897jd0.emergentagent.net/job_dbbb4f88-5ac8-4fde-9c1a-a326bcf136ff/artifacts/6srq43i2_AIR%20CAIRO_HAIL.webp",
        title: "Hail Damage Analysis",
        description: "Automated detection and mapping of impact damage across the fuselage, wings, and nose cone — with per-frame findings, severity ratings, and confidence scores ready for insurance and MRO handoff."
      },
      {
        type: "image",
        src: "https://customer-assets-jt897jd0.emergentagent.net/job_dbbb4f88-5ac8-4fde-9c1a-a326bcf136ff/artifacts/suidmofr_Screenshot%202026-08-23%20at%205.59.03%E2%80%AFPM.png",
        title: "Hail Damage Report",
        description: "A structured FAA Form 8130-3 Authorized Release Certificate — capturing aircraft details, anomaly distribution, and per-finding severity, confidence, and repair recommendations, ready for MRO handoff."
      }
    ]
  },
  {
    label: "737 Exterior Inspection",
    chapters: [
      {
        type: "video",
        src: "/videos/737-exterior-inspection.mp4",
        poster: "/posters/737-exterior.jpg",
        title: "737 Exterior Capture",
        description: "End-to-end exterior walk-around inspection for narrow-body commercial fleets — paint condition, rivets, panel gaps, and antenna integrity, captured with a hardware-agnostic camera."
      },
      {
        type: "image",
        src: "https://customer-assets-jt897jd0.emergentagent.net/job_dbbb4f88-5ac8-4fde-9c1a-a326bcf136ff/artifacts/yurz1hrz_B737.webp",
        title: "737 Exterior Analysis",
        description: "Tail-number-linked inspection history with findings trends over time — total inspections, average findings, recurring issues, and overall trend at a glance."
      },
      {
        type: "image",
        src: "https://customer-assets-jt897jd0.emergentagent.net/job_dbbb4f88-5ac8-4fde-9c1a-a326bcf136ff/artifacts/zl0ads8h_Screenshot%202026-08-23%20at%206.56.21%E2%80%AFPM.png",
        title: "737 Exterior Report",
        description: "A structured FAA Form 8130-3 Authorized Release Certificate with a full inspection report summary — total pages, frames with findings, anomalies detected, and per-finding severity, confidence, and repair recommendations."
      }
    ]
  },
  {
    label: "Military",
    type: "gated",
    title: "Restricted Access",
    description: "Military and defense inspection demos are ITAR-compliant and only available to verified defense partners. Request access to view sub-millimeter capture, analysis, and reporting for military airframes."
  }
];

export const DemoSection = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [activeDemo, setActiveDemo] = useState(0);
  const [activeDemoChapter, setActiveDemoChapter] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % demoSlides.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [activeSlide]);

  return (
    <section id="demo" data-testid="demo-showcase-section" className="py-20 px-6 bg-gradient-to-b from-white to-orange-50/40">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center space-y-4 mb-12">
          <Badge className="bg-orange-100 text-orange-700 border-orange-200">
            <Play className="w-3 h-3 mr-1" />
            Product Demo
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-black">See ArcheanX in Action</h2>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto">
            Watch how our AI-powered platform captures, analyzes, and documents aircraft inspections end-to-end.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 items-center">
          {/* Video panel */}
          <div className="lg:col-span-3 relative">
            <div className="relative bg-black rounded-2xl overflow-hidden shadow-2xl aspect-video border border-gray-200">
              {demoSlides.map((slide, idx) => (
                slide.type === "image" ? (
                  <img
                    key={idx}
                    data-testid={`demo-media-${idx}`}
                    src={slide.src}
                    alt={slide.title}
                    className={`absolute inset-0 w-full h-full object-contain bg-white transition-opacity duration-700 ${
                      idx === activeSlide ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    }`}
                  />
                ) : (
                  <VideoWithFallback
                    key={idx}
                    testId={`demo-media-${idx}`}
                    src={slide.video}
                    poster={slide.poster}
                    alt={slide.title}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                      idx === activeSlide ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    }`}
                  />
                )
              ))}
              {/* Progress bar */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
                <div
                  key={activeSlide}
                  className="h-full bg-orange-500 origin-left"
                  style={{ animation: 'demoProgress 8s linear' }}
                />
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-between mt-5">
              <button
                data-testid="demo-prev-btn"
                onClick={() => setActiveSlide((prev) => (prev - 1 + demoSlides.length) % demoSlides.length)}
                className="w-10 h-10 rounded-full border border-gray-300 bg-white hover:border-orange-500 hover:text-orange-600 text-gray-700 flex items-center justify-center transition-colors"
                aria-label="Previous slide"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              <div className="flex items-center gap-2">
                {demoSlides.map((_, idx) => (
                  <button
                    key={idx}
                    data-testid={`demo-dot-${idx}`}
                    onClick={() => setActiveSlide(idx)}
                    aria-label={`Go to slide ${idx + 1}`}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      idx === activeSlide ? 'w-8 bg-orange-500' : 'w-2 bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>

              <button
                data-testid="demo-next-btn"
                onClick={() => setActiveSlide((prev) => (prev + 1) % demoSlides.length)}
                className="w-10 h-10 rounded-full border border-gray-300 bg-white hover:border-orange-500 hover:text-orange-600 text-gray-700 flex items-center justify-center transition-colors"
                aria-label="Next slide"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Description panel */}
          <div className="lg:col-span-2 space-y-5">
            <div className="text-sm font-semibold text-orange-600 uppercase tracking-wider">
              Chapter {String(activeSlide + 1).padStart(2, '0')} / {String(demoSlides.length).padStart(2, '0')}
            </div>
            <h3 key={`title-${activeSlide}`} data-testid="demo-slide-title" className="text-3xl md:text-4xl font-bold text-black leading-tight animate-fade-in">
              {demoSlides[activeSlide].title}
            </h3>
            <p key={`desc-${activeSlide}`} data-testid="demo-slide-desc" className="text-base text-gray-700 leading-relaxed animate-fade-in">
              {demoSlides[activeSlide].description}
            </p>
            <div className="pt-2">
              <Button
                data-testid="demo-request-btn"
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Request Personalized Demo <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>

        {/* Inspection Demos - Tabbed subsection */}
        <div className="mt-20 pt-16 border-t border-gray-200">
          <div className="text-center mb-10">
            <Badge className="bg-orange-100 text-orange-700 border-orange-200 mb-3">
              Inspection Demos
            </Badge>
            <h3 className="text-3xl md:text-4xl font-bold text-black">Explore by inspection type</h3>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {inspectionDemos.map((demo, idx) => (
              <button
                key={idx}
                data-testid={`inspection-tab-${idx}`}
                onClick={() => { setActiveDemo(idx); setActiveDemoChapter(0); }}
                className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 ${
                  idx === activeDemo
                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/30'
                    : 'bg-white border border-gray-300 text-gray-700 hover:border-orange-500 hover:text-orange-600'
                }`}
              >
                {demo.label}
              </button>
            ))}
          </div>

          {/* Active media + description */}
          <div className="grid lg:grid-cols-5 gap-8 items-center">
            <div className="lg:col-span-3">
              {(() => {
                const demo = inspectionDemos[activeDemo];
                const hasChapters = Array.isArray(demo.chapters);
                const chIdx = hasChapters ? Math.min(activeDemoChapter, demo.chapters.length - 1) : 0;
                const currentMedia = hasChapters ? demo.chapters[chIdx] : demo;
                return (
                  <>
                    <div className="relative bg-black rounded-2xl overflow-hidden shadow-xl aspect-video border border-gray-200">
                      {currentMedia.type === "video" ? (
                        <VideoWithFallback
                          key={`${activeDemo}-${chIdx}-video`}
                          testId={`inspection-media-${activeDemo}`}
                          src={currentMedia.src}
                          poster={currentMedia.poster}
                          alt={currentMedia.title}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      ) : currentMedia.type === "gated" ? (
                        <div
                          data-testid={`inspection-media-${activeDemo}`}
                          className="absolute inset-0 w-full h-full flex flex-col items-center justify-center gap-4 bg-gradient-to-br from-gray-900 to-black px-8 text-center"
                        >
                          <div className="w-16 h-16 rounded-full bg-orange-500/10 border border-orange-500/30 flex items-center justify-center">
                            <Lock className="w-7 h-7 text-orange-500" />
                          </div>
                          <p className="text-white text-lg font-semibold">Restricted Content</p>
                          <p className="text-gray-400 text-sm max-w-sm">
                            Military inspection demos are only available to verified defense partners.
                          </p>
                          <Button
                            data-testid="inspection-request-access-btn"
                            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
                            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                          >
                            Request Access <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </div>
                      ) : (
                        <img
                          key={`${activeDemo}-${chIdx}-img`}
                          data-testid={`inspection-media-${activeDemo}`}
                          src={currentMedia.src}
                          alt={currentMedia.title || demo.label}
                          className="absolute inset-0 w-full h-full object-contain bg-white"
                        />
                      )}
                    </div>

                    {/* Chapter controls - only when active tab has multiple chapters */}
                    {hasChapters && demo.chapters.length > 1 && (
                      <div className="flex items-center justify-between mt-5">
                        <button
                          data-testid="inspection-chapter-prev"
                          onClick={() => setActiveDemoChapter((prev) => (prev - 1 + demo.chapters.length) % demo.chapters.length)}
                          className="w-10 h-10 rounded-full border border-gray-300 bg-white hover:border-orange-500 hover:text-orange-600 text-gray-700 flex items-center justify-center transition-colors"
                          aria-label="Previous chapter"
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>

                        <div className="flex items-center gap-2">
                          {demo.chapters.map((_, cIdx) => (
                            <button
                              key={cIdx}
                              data-testid={`inspection-chapter-dot-${cIdx}`}
                              onClick={() => setActiveDemoChapter(cIdx)}
                              aria-label={`Chapter ${cIdx + 1}`}
                              className={`h-2 rounded-full transition-all duration-300 ${
                                cIdx === chIdx ? 'w-8 bg-orange-500' : 'w-2 bg-gray-300 hover:bg-gray-400'
                              }`}
                            />
                          ))}
                        </div>

                        <button
                          data-testid="inspection-chapter-next"
                          onClick={() => setActiveDemoChapter((prev) => (prev + 1) % demo.chapters.length)}
                          className="w-10 h-10 rounded-full border border-gray-300 bg-white hover:border-orange-500 hover:text-orange-600 text-gray-700 flex items-center justify-center transition-colors"
                          aria-label="Next chapter"
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            <div className="lg:col-span-2 space-y-4">
              {(() => {
                const demo = inspectionDemos[activeDemo];
                const hasChapters = Array.isArray(demo.chapters);
                const chIdx = hasChapters ? Math.min(activeDemoChapter, demo.chapters.length - 1) : 0;
                const currentMedia = hasChapters ? demo.chapters[chIdx] : demo;
                return (
                  <>
                    <div data-testid="inspection-chapter-label" className="text-sm font-semibold text-orange-600 uppercase tracking-wider">
                      {hasChapters
                        ? `${demo.label} · Chapter ${String(chIdx + 1).padStart(2, '0')} / ${String(demo.chapters.length).padStart(2, '0')}`
                        : demo.label}
                    </div>
                    <h4 key={`insp-title-${activeDemo}-${chIdx}`} data-testid="inspection-demo-title" className="text-2xl md:text-3xl font-bold text-black leading-tight animate-fade-in">
                      {currentMedia.title || `${demo.label} Demo`}
                    </h4>
                    <p key={`insp-desc-${activeDemo}-${chIdx}`} data-testid="inspection-demo-desc" className="text-base text-gray-700 leading-relaxed animate-fade-in">
                      {currentMedia.description || demo.description}
                    </p>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
